import type { ApiKeyRole } from './api-key-roles.service';

export type ApiKeyScopeGroup = {
  resource: string;
  label: string;
  options: { value: string; label: string }[];
};

const ROLE_PATTERN = /^(.+)_(all|read|write|delete|create|update|send|admin)$/i;

const formatRolePart = (value: string) =>
  value.replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

export function getRoleRuleDefaults(role: ApiKeyRole) {
  const match = role.slug.match(ROLE_PATTERN);
  if (!match) return null;
  const action = match[2].toLowerCase();
  return {
    GroupKey: match[1],
    GroupLabel: formatRolePart(match[1]),
    Label: formatRolePart(action),
    IsGroupAll: action === 'all',
  };
}

export function groupApiKeyRoles(roles: ApiKeyRole[]): ApiKeyScopeGroup[] {
  const groups = new Map<string, ApiKeyScopeGroup>();
  const seen = new Set<string>();
  for (const role of roles) {
    if (seen.has(role.slug)) continue;
    seen.add(role.slug);
    const match = role.slug.match(ROLE_PATTERN);
    if (!match) continue;
    const resource = match[1];
    const label = formatRolePart(resource);
    const group = groups.get(resource) ?? { resource, label, options: [] };
    const prefix = `${label} `;
    const optionLabel = role.name.toLowerCase().startsWith(prefix.toLowerCase())
      ? role.name.slice(prefix.length).trim()
      : role.name;
    group.options.push({ value: role.slug, label: optionLabel || role.name });
    groups.set(resource, group);
  }
  return [...groups.values()]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((group) => ({
      ...group,
      options: group.options.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}
